import { defaultLocale, isLocale, type Locale } from '@/i18n'

// Split an internal href into its path and its ?query#hash suffix, so we only
// ever touch the path half.
const PATH_AND_SUFFIX = /^([^?#]*)(.*)$/

// Prefix an internal href with the current locale so internal links inside a
// non-default-locale render emit /de/foo instead of /foo. Without this, a
// German page links back into the English tree, the user gets a proxy redirect,
// and bots without the ht_locale cookie just follow the English link — so
// PageRank never flows within the de tree.
//
// Two normalizations, both there to keep us off a 308:
//   - the root path yields `/de`, not `/de/` (prod redirects `/de/` → `/de`);
//   - a trailing slash is dropped (`/hearing-aids/` → `/de/hearing-aids`).
// Getting the root wrong is not academic: it put a redirecting link in the
// footer of every page on the site.
export const localizedHref = (locale: Locale, href: string): string => {
  // Relative, external, protocol-relative, mailto: — not ours to rewrite.
  if (!href.startsWith('/') || href.startsWith('//')) {
    return href
  }

  const [, rawPath, suffix] = PATH_AND_SUFFIX.exec(href) as RegExpExecArray
  // '' for the root, so `/${locale}${path}` composes to `/de` rather than `/de/`.
  const path = rawPath.replace(/\/+$/, '')

  if (locale === defaultLocale) {
    return `${path || '/'}${suffix}`
  }

  const firstSegment = path.split('/')[1]
  if (firstSegment && isLocale(firstSegment)) {
    return `${path}${suffix}`
  }

  return `/${locale}${path}${suffix}`
}
